import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/store/authStore'
import { handleApiError } from '@/api/client'
import type { RegisterRequest } from '@/types/api'

const registerSchema = z.object({
  email: z.string().email('올바른 이메일을 입력하세요'),
  password: z.string().min(8, '비밀번호는 8자 이상이어야 합니다'),
  confirmPassword: z.string(),
  fullName: z.string().min(2, '이름을 입력하세요'),
  phoneNumber: z.string().optional(),
  marketingAgreed: z.boolean().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: '비밀번호가 일치하지 않습니다',
  path: ['confirmPassword'],
})

type RegisterFormData = z.infer<typeof registerSchema>

export function RegisterPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [error, setError] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors } } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  })

  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
    onSuccess: (data) => {
      login(data.user, data.accessToken, data.refreshToken)
      navigate('/', { replace: true })
    },
    onError: (error) => {
      setError(handleApiError(error))
    },
  })

  const onSubmit = (data: RegisterFormData) => {
    setError(null)
    const { confirmPassword, ...registerData } = data
    registerMutation.mutate(registerData)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-primary-600">Quebon</h1>
          <h2 className="mt-6 text-2xl font-semibold text-gray-900">회원가입</h2>
          <p className="mt-2 text-gray-600">새 계정을 만드세요</p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="fullName" className="label">이름</label>
              <input
                id="fullName"
                type="text"
                className={`input ${errors.fullName ? 'input-error' : ''}`}
                placeholder="홍길동"
                {...register('fullName')}
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600">{errors.fullName.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="label">이메일</label>
              <input
                id="email"
                type="email"
                className={`input ${errors.email ? 'input-error' : ''}`}
                placeholder="email@example.com"
                {...register('email')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="label">비밀번호</label>
              <input
                id="password"
                type="password"
                className={`input ${errors.password ? 'input-error' : ''}`}
                placeholder="8자 이상 입력하세요"
                {...register('password')}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">비밀번호 확인</label>
              <input
                id="confirmPassword"
                type="password"
                className={`input ${errors.confirmPassword ? 'input-error' : ''}`}
                placeholder="비밀번호를 다시 입력하세요"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="phoneNumber" className="label">휴대폰 번호 (선택)</label>
              <input
                id="phoneNumber"
                type="tel"
                className="input"
                placeholder="010-1234-5678"
                {...register('phoneNumber')}
              />
            </div>

            <div className="flex items-center">
              <input
                id="marketingAgreed"
                type="checkbox"
                className="w-4 h-4 text-primary-600 border-gray-300 rounded-sm focus:ring-primary-500"
                {...register('marketingAgreed')}
              />
              <label htmlFor="marketingAgreed" className="ml-2 text-sm text-gray-600">
                마케팅 정보 수신에 동의합니다 (선택)
              </label>
            </div>

            <button
              type="submit"
              disabled={registerMutation.isPending}
              className="w-full btn btn-primary btn-lg"
            >
              {registerMutation.isPending ? '가입 중...' : '회원가입'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            이미 계정이 있으신가요?{' '}
            <Link to="/login" className="text-primary-600 hover:underline font-medium">
              로그인
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}


